import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type {
  CallContact,
  CallContactInput,
  CallContactKind,
  CallContactScope,
} from "@shared/types/foyer";
import { foyerApi, type RoomTypeRecord } from "../http/foyer";
import AssetField from "./AssetField";
import TimezoneSelect from "./TimezoneSelect";
import { ItemFormSurface, type Dismiss } from "./EditDrawer";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import { errorText } from "../utils/apiError";

/**
 * Create/edit one directory entry (VC-10).
 *
 * Two things here are load-bearing rather than cosmetic:
 *
 *  1. **First names only.** `displayName` is the one field that is NOT
 *     encrypted, because a TV has to render it - and that TV stands in a room
 *     that visitors, cleaners and the next resident walk through. The helper
 *     text says so at the point of typing, not in a tooltip somebody may never
 *     open.
 *  2. **Auto-answer cannot be armed by accident.** It opens a camera in a
 *     bedroom with nobody accepting, so the checkbox is disabled unless the
 *     premise switch (VC-09) is on AND the contact is staff - and the tooltip
 *     always states which of the two is missing, because a control that is
 *     merely greyed out reads as a bug.
 *
 * Scope is immutable after creation (the server enforces it): re-pointing a row
 * would transplant a relationship - and its consent record - onto someone else.
 */

/** Family lives in the resident flow (VC-11); this page is staff + services. */
const KIND_OPTIONS: {
  value: CallContactKind;
  label: "staff" | "service";
  hint: "staffHint" | "serviceHint";
}[] = [
  { value: "staff", label: "staff", hint: "staffHint" },
  { value: "service", label: "service", hint: "serviceHint" },
];

const SCOPE_OPTIONS: {
  value: CallContactScope;
  label: "premise" | "roomType";
}[] = [
  { value: "premise", label: "premise" },
  { value: "roomType", label: "roomType" },
];

export interface CallContactDraft {
  scope: CallContactScope;
  roomTypeId: string;
  displayName: string;
  relation: string;
  avatarAssetId: string;
  kind: CallContactKind;
  callableFromRoom: boolean;
  canCallRoom: boolean;
  autoAnswer: boolean;
  priority: string;
  activeFrom: string;
  activeUntil: string;
  quietFrom: string;
  quietTo: string;
  quietTz: string;
  phoneE164: string;
  email: string;
  foyerUserId: string;
}

/** ISO → the value a `datetime-local` input wants (local wall clock). */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** `datetime-local` → ISO, or null when the operator left it empty. */
function fromLocalInput(v: string): string | null {
  if (!v.trim()) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function emptyDraft(defaultTz: string): CallContactDraft {
  return {
    scope: "premise",
    roomTypeId: "",
    displayName: "",
    relation: "",
    avatarAssetId: "",
    kind: "staff",
    callableFromRoom: true,
    canCallRoom: false,
    autoAnswer: false,
    priority: "0",
    activeFrom: "",
    activeUntil: "",
    quietFrom: "",
    quietTo: "",
    quietTz: defaultTz,
    phoneE164: "",
    email: "",
    foyerUserId: "",
  };
}

function draftOf(c: CallContact, defaultTz: string): CallContactDraft {
  return {
    scope: c.scope === "person" ? "premise" : c.scope,
    roomTypeId: c.roomTypeId ?? "",
    displayName: c.displayName,
    relation: c.relation ?? "",
    avatarAssetId: c.avatarAssetId ?? "",
    kind: c.kind === "family" ? "staff" : c.kind,
    callableFromRoom: c.callableFromRoom,
    canCallRoom: c.canCallRoom,
    autoAnswer: c.autoAnswer,
    priority: String(c.priority ?? 0),
    activeFrom: toLocalInput(c.activeFrom),
    activeUntil: toLocalInput(c.activeUntil),
    quietFrom: c.quietHours?.from ?? "",
    quietTo: c.quietHours?.to ?? "",
    quietTz: c.quietHours?.tz ?? defaultTz,
    phoneE164: "",
    email: "",
    foyerUserId: "",
  };
}

interface Props {
  open: boolean;
  premiseId: string;
  /** Null = create. */
  contact: CallContact | null;
  /** Premise timezone, prefilled into quiet hours so it's rarely retyped. */
  defaultTz: string;
  /** VC-09 premise master switch - auto-answer is inert (and disabled) when off. */
  allowAutoAnswer: boolean;
  onClose: () => void;
  /** Receives the finished success sentence, ready to toast. */
  onSaved: (message: string) => void;
}

export default function CallContactDialog({
  open,
  premiseId,
  contact,
  defaultTz,
  allowAutoAnswer,
  onClose,
  onSaved,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const [draft, setDraft] = useState<CallContactDraft>(() =>
    emptyDraft(defaultTz)
  );
  const [roomTypes, setRoomTypes] = useState<RoomTypeRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const editing = !!contact;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDraft(contact ? draftOf(contact, defaultTz) : emptyDraft(defaultTz));
  }, [open, contact, defaultTz]);

  useEffect(() => {
    if (!open) return;
    foyerApi
      .listRoomTypes()
      .then(setRoomTypes)
      .catch(() => setRoomTypes([]));
  }, [open]);

  const set = <K extends keyof CallContactDraft>(
    key: K,
    value: CallContactDraft[K]
  ) => setDraft((d) => ({ ...d, [key]: value }));

  const hasEndpoint =
    !!draft.phoneE164.trim() ||
    !!draft.email.trim() ||
    !!draft.foyerUserId.trim();

  // Editing keeps the stored (encrypted) endpoints when the fields are blank -
  // they are never read back into the form, so "empty" must mean "unchanged".
  const endpointsOk = editing || hasEndpoint;

  const quietPartial =
    [draft.quietFrom, draft.quietTo].filter((v) => v.trim()).length === 1;

  const valid =
    !!draft.displayName.trim() &&
    (draft.scope !== "roomType" || !!draft.roomTypeId) &&
    endpointsOk &&
    !quietPartial;

  const autoAnswerBlocked = !allowAutoAnswer || draft.kind !== "staff";
  const autoAnswerReason = useMemo(() => {
    if (!allowAutoAnswer) {
      return t("callContactDialog.autoAnswer.offForPremise");
    }
    if (draft.kind !== "staff") {
      return t("callContactDialog.autoAnswer.staffOnly");
    }
    return t("callContactDialog.autoAnswer.joins");
  }, [allowAutoAnswer, draft.kind, t]);

  // Flipping away from staff disarms auto-answer here too, so the form never
  // shows an armed checkbox the server is about to refuse.
  useEffect(() => {
    if (autoAnswerBlocked && draft.autoAnswer) {
      setDraft((d) => ({ ...d, autoAnswer: false }));
    }
  }, [autoAnswerBlocked, draft.autoAnswer]);

  async function submit(dismiss: Dismiss) {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const quietHours =
      draft.quietFrom.trim() && draft.quietTo.trim()
        ? {
            from: draft.quietFrom.trim(),
            to: draft.quietTo.trim(),
            tz: draft.quietTz.trim() || defaultTz,
          }
        : null;
    const priority = Number(draft.priority);
    const common = {
      displayName: draft.displayName.trim(),
      relation: draft.relation.trim() || null,
      avatarAssetId: draft.avatarAssetId.trim() || null,
      kind: draft.kind,
      callableFromRoom: draft.callableFromRoom,
      canCallRoom: draft.canCallRoom,
      autoAnswer: draft.autoAnswer,
      priority: Number.isFinite(priority) ? priority : 0,
      quietHours,
      activeFrom: fromLocalInput(draft.activeFrom),
      activeUntil: fromLocalInput(draft.activeUntil),
    };
    const endpoints = {
      ...(draft.phoneE164.trim() ? { phoneE164: draft.phoneE164.trim() } : {}),
      ...(draft.email.trim() ? { email: draft.email.trim() } : {}),
      ...(draft.foyerUserId.trim()
        ? { foyerUserId: draft.foyerUserId.trim() }
        : {}),
    };
    try {
      if (contact) {
        await foyerApi.updateCallContact(premiseId, contact.id, {
          ...common,
          // Blank endpoint fields mean "keep what is stored", not "erase".
          ...(hasEndpoint ? { endpoints } : {}),
        });
        // Slide out first, then let the page react and close.
        dismiss(() => {
          onSaved(t("callContactDialog.updated", { name: common.displayName }));
          onClose();
        });
      } else {
        const input: CallContactInput = {
          scope: draft.scope,
          roomTypeId: draft.scope === "roomType" ? draft.roomTypeId : null,
          endpoints,
          ...common,
        };
        await foyerApi.createCallContact(premiseId, input);
        dismiss(() => {
          onSaved(t("callContactDialog.added", { name: common.displayName }));
          onClose();
        });
      }
    } catch (err) {
      setError(errorText(err, t));
      toast.error(
        describeError(t, "saveContact", err, { name: common.displayName })
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    // Add contact is a modal; editing an existing one slides in from the right.
    <ItemFormSurface
      mode={editing ? "edit" : "create"}
      open={open}
      onClose={onClose}
      busy={busy}
      maxWidth="sm"
      title={
        editing
          ? t("callContactDialog.titleEdit")
          : t("callContactDialog.addContact")
      }
      data-testid="call-contact-dialog"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void submit(dismiss)}
            disabled={!valid || busy}
            data-testid="contact-submit"
          >
            {busy
              ? t("common:states.saving")
              : editing
                ? t("common:buttons.save")
                : t("callContactDialog.addContact")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2.5} sx={{ mt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          size="small"
          label={t("callContactDialog.displayName")}
          value={draft.displayName}
          onChange={(e) => set("displayName", e.target.value)}
          fullWidth
          autoFocus
          required
          helperText={t("callContactDialog.displayNameHelp")}
          inputProps={{ "data-testid": "contact-display-name" }}
        />
        <TextField
          size="small"
          label={t("callContactDialog.relation")}
          placeholder={t("callContactDialog.relationPlaceholder")}
          value={draft.relation}
          onChange={(e) => set("relation", e.target.value)}
          fullWidth
          inputProps={{ "data-testid": "contact-relation" }}
        />

        <FormControl size="small" fullWidth>
          <InputLabel id="contact-kind-label">
            {t("callContactDialog.kindLabel")}
          </InputLabel>
          <Select
            labelId="contact-kind-label"
            label={t("callContactDialog.kindLabel")}
            value={draft.kind}
            onChange={(e) => set("kind", e.target.value as CallContactKind)}
            data-testid="contact-kind"
          >
            {KIND_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t("callContactDialog.kind.option", {
                  label: t(`callContactDialog.kind.${o.label}`),
                  hint: t(`callContactDialog.kind.${o.hint}`),
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <AssetField
            label={t("callContactDialog.avatar")}
            value={draft.avatarAssetId}
            onChange={(v) => set("avatarAssetId", v)}
            testId="contact-avatar"
            preview
          />
        </Box>

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">
            {t("callContactDialog.whereApplies")}
          </Typography>
        </Divider>

        <FormControl size="small" fullWidth disabled={editing}>
          <InputLabel id="contact-scope-label">
            {t("callContactDialog.scopeLabel")}
          </InputLabel>
          <Select
            labelId="contact-scope-label"
            label={t("callContactDialog.scopeLabel")}
            value={draft.scope}
            onChange={(e) => set("scope", e.target.value as CallContactScope)}
            data-testid="contact-scope"
          >
            {SCOPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(`callContactDialog.scope.${o.label}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {draft.scope === "roomType" && (
          <FormControl size="small" fullWidth disabled={editing} required>
            <InputLabel id="contact-room-type-label">
              {t("callContactDialog.unitType")}
            </InputLabel>
            <Select
              labelId="contact-room-type-label"
              label={t("callContactDialog.unitType")}
              value={draft.roomTypeId}
              onChange={(e) => set("roomTypeId", e.target.value)}
              data-testid="contact-room-type"
            >
              {roomTypes.length === 0 && (
                <MenuItem value="" disabled>
                  {t("callContactDialog.noUnitTypes")}
                </MenuItem>
              )}
              {roomTypes.map((rt) => (
                <MenuItem key={rt.id} value={rt.id}>
                  {rt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {editing && (
          <Typography variant="caption" color="text.secondary">
            {t("callContactDialog.scopeLocked")}
          </Typography>
        )}

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">
            {t("callContactDialog.directions")}
          </Typography>
        </Divider>

        <Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.callableFromRoom}
                onChange={(e) => set("callableFromRoom", e.target.checked)}
                data-testid="contact-callable-from-room"
              />
            }
            label={t("callContactDialog.callable")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.canCallRoom}
                onChange={(e) => set("canCallRoom", e.target.checked)}
                data-testid="contact-can-call-room"
              />
            }
            label={t("callContactDialog.canRing")}
          />
          <Tooltip title={autoAnswerReason}>
            <span>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.autoAnswer}
                    disabled={autoAnswerBlocked}
                    onChange={(e) => set("autoAnswer", e.target.checked)}
                    inputProps={
                      {
                        "data-testid": "contact-auto-answer",
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label={t("callContactDialog.autoAnswerLabel")}
              />
            </span>
          </Tooltip>
        </Stack>

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">
            {t("callContactDialog.howReach")}
          </Typography>
        </Divider>

        <Stack spacing={2}>
          <TextField
            size="small"
            label={t("callContactDialog.phone")}
            placeholder="+46701234567"
            value={draft.phoneE164}
            onChange={(e) => set("phoneE164", e.target.value)}
            fullWidth
            inputProps={{ "data-testid": "contact-phone" }}
          />
          <TextField
            size="small"
            label={t("callContactDialog.email")}
            value={draft.email}
            onChange={(e) => set("email", e.target.value)}
            fullWidth
            inputProps={{ "data-testid": "contact-email" }}
          />
          <TextField
            size="small"
            label={t("callContactDialog.foyerUserId")}
            value={draft.foyerUserId}
            onChange={(e) => set("foyerUserId", e.target.value)}
            fullWidth
            inputProps={{ "data-testid": "contact-foyer-user" }}
          />
          <Typography variant="caption" color="text.secondary">
            {editing
              ? t("callContactDialog.endpointsEdit")
              : t("callContactDialog.endpointsCreate")}
          </Typography>
        </Stack>

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">
            {t("callContactDialog.whenApplies")}
          </Typography>
        </Divider>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            size="small"
            type="datetime-local"
            label={t("callContactDialog.activeFrom")}
            value={draft.activeFrom}
            onChange={(e) => set("activeFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ "data-testid": "contact-active-from" }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label={t("callContactDialog.activeUntil")}
            value={draft.activeUntil}
            onChange={(e) => set("activeUntil", e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ "data-testid": "contact-active-until" }}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            size="small"
            type="time"
            label={t("callContactDialog.quietFrom")}
            value={draft.quietFrom}
            onChange={(e) => set("quietFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ "data-testid": "contact-quiet-from" }}
          />
          <TextField
            size="small"
            type="time"
            label={t("callContactDialog.quietTo")}
            value={draft.quietTo}
            onChange={(e) => set("quietTo", e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ "data-testid": "contact-quiet-to" }}
          />
          <TimezoneSelect
            value={draft.quietTz}
            onChange={(tz) => set("quietTz", tz || defaultTz)}
            testId="contact-quiet-tz"
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t("callContactDialog.quietHelp")}
        </Typography>

        <TextField
          size="small"
          type="number"
          label={t("callContactDialog.priority")}
          value={draft.priority}
          onChange={(e) => set("priority", e.target.value)}
          helperText={t("callContactDialog.priorityHelp")}
          sx={{ maxWidth: 200 }}
          inputProps={{ "data-testid": "contact-priority" }}
        />
      </Stack>
    </ItemFormSurface>
  );
}
